import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Loader2, FileText, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { abdmApi } from "../lib/api";
import { useAuthStore } from "../store/authStore";

const HI_TYPES = ["DiagnosticReport", "Prescription", "OPConsultation"];
const STATUS_COLOR = {
  REQUESTED: "text-amber-600", INIT_ACK: "text-amber-600", GRANTED: "text-emerald-600",
  FETCHED: "text-emerald-600", DENIED: "text-red-600", EXPIRED: "text-muted-foreground",
};

export default function FetchAbha() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const userId = user?._id;

  const [abha, setAbha] = useState("");
  const [types, setTypes] = useState(["DiagnosticReport", "Prescription"]);
  const [busy, setBusy] = useState(false);
  const [consents, setConsents] = useState([]);
  const [records, setRecords] = useState([]);
  const [fetchingId, setFetchingId] = useState(null);

  const loadConsents = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await abdmApi.hiuConsents(userId);
      setConsents(res.data.consents || []);
    } catch { /* noop */ }
  }, [userId]);

  useEffect(() => { loadConsents(); }, [loadConsents]);

  const toggleType = (t) => setTypes((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));

  const requestConsent = async () => {
    if (!abha.trim()) return toast.error("Enter the ABHA address to request records for");
    if (types.length === 0) return toast.error("Pick at least one record type");
    setBusy(true);
    try {
      const to = new Date().toISOString();
      const from = new Date(Date.now() - 365 * 864e5).toISOString();
      await abdmApi.hiuRequestConsent({ patient_id: userId, abha_address: abha.trim(), hi_types: types, date_from: from, date_to: to });
      toast.success("Consent request sent — approve it in your ABHA (PHR) app to share the records.");
      loadConsents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send consent request");
    } finally {
      setBusy(false);
    }
  };

  const fetchRecords = async (c) => {
    setFetchingId(c.consentId || c.id);
    try {
      const res = await abdmApi.hiuFetchRecords(c.consentId);
      const txn = res.data.transactionId;
      // give the async transfer a moment, then read what arrived
      await new Promise((r) => setTimeout(r, 2500));
      const rec = await abdmApi.hiuRecords(txn);
      setRecords(rec.data.records || []);
      toast.success(`Fetched ${rec.data.records?.length || 0} record(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Records not ready yet — try again shortly");
    } finally {
      setFetchingId(null);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="fetch-abha-back"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="text-lg font-bold">Fetch Records via ABHA</h1>
      </div>

      <Card className="rounded-2xl p-3 flex items-start gap-2 bg-sky-50 border-sky-100">
        <ShieldCheck className="h-5 w-5 text-sky-600 shrink-0 mt-0.5" />
        <p className="text-sm text-sky-800">Request a patient&apos;s records from other ABDM providers. They approve the consent in their ABHA app, then the records are fetched securely.</p>
      </Card>

      <Card className="rounded-2xl p-4 space-y-3">
        <div>
          <label className="text-sm font-medium">ABHA address</label>
          <input value={abha} onChange={(e) => setAbha(e.target.value)} placeholder="name@sbx"
            className="w-full mt-1 rounded-xl border px-3 py-2 text-sm" data-testid="fetch-abha-input" />
        </div>
        <div>
          <p className="text-sm font-medium mb-1">Record types</p>
          <div className="flex flex-wrap gap-2">
            {HI_TYPES.map((t) => (
              <button key={t} onClick={() => toggleType(t)} data-testid={`fetch-type-${t}`}
                className={`px-3 py-1.5 rounded-full text-sm border ${types.includes(t) ? "border-primary bg-primary/10 font-semibold" : "border-border"}`}>{t}</button>
            ))}
          </div>
        </div>
        <Button onClick={requestConsent} disabled={busy} className="rounded-full w-full" data-testid="fetch-request-consent">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Request Consent
        </Button>
      </Card>

      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">Your consent requests</p>
        <button onClick={loadConsents} className="text-muted-foreground" data-testid="fetch-refresh"><RefreshCw className="h-4 w-4" /></button>
      </div>
      {consents.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="fetch-consents-empty">No consent requests yet.</p>
      ) : (
        <div className="space-y-2">
          {consents.map((c) => (
            <Card key={c.id} className="rounded-2xl p-3 flex items-center gap-3" data-testid={`consent-${c.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{(c.hiTypes || []).join(", ") || "Records"}</p>
                <p className={`text-xs font-semibold ${STATUS_COLOR[c.status] || "text-muted-foreground"}`}>{c.status}</p>
              </div>
              {["GRANTED", "FETCHED"].includes(c.status) && c.consentId && (
                <Button size="sm" variant="outline" className="rounded-full shrink-0" disabled={fetchingId === c.consentId}
                  onClick={() => fetchRecords(c)} data-testid={`consent-fetch-${c.id}`}>
                  {fetchingId === c.consentId ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1" /> Fetch</>}
                </Button>
              )}
            </Card>
          ))}
        </div>
      )}

      {records.length > 0 && (
        <div className="space-y-2" data-testid="fetch-records">
          <p className="text-sm font-semibold">Fetched records</p>
          {records.map((r, i) => (
            <Card key={i} className="rounded-2xl p-3 flex items-center gap-3">
              <FileText className="h-5 w-5 text-sky-600 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{r.careContextReference || "Health record"}</p>
                <p className="text-xs text-muted-foreground">{r.bundle?.entry?.[0]?.resource?.title || r.bundle?.type || "FHIR bundle"}</p>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
